import { DateTime } from 'luxon'

import { DeliveryDateType, Order, Package, PackageEventOperations } from '@consumer/graphql'
import { misdeliverySubheader, packageHadDeliveryIssueHeader, packageHadDeliveryIssueSubheader } from '@consumer/shared'

export interface HeaderResult {
  header: string
  subheader: string | null
}

export const headerFromPackageStatus = ({
  pkg,
  order,
  clientDisplayName,
  deliveryDate,
}: {
  pkg: Package
  order: Order
  clientDisplayName: string
  deliveryDate: string | null | undefined
}): HeaderResult | undefined => {
  const mostRecentPkgStatus = pkg?.eventLog[0]?.operation
  const mostRecentPkgMessage = pkg?.eventLog[0]?.message

  // Calculate delivered timestamp if package is delivered
  const isDelivered = pkg?.eventLog[0]?.operation === PackageEventOperations.Delivered
  const deliveredTimestamp =
    isDelivered && pkg?.eventLog[0]?.timestamp
      ? DateTime.fromISO(pkg.eventLog[0].timestamp)
          .setZone(pkg?.timeZone ?? order.timeZone!)
          .toLocaleString(DateTime.DATETIME_FULL)
      : null

  const getPkgExpectedDeliveryDate = ({ pkg, order }: { pkg: Package; order: Order }): HeaderResult => {
    // First check if we have a delivery date info
    // If we do, use that to format the header
    const timeZone = pkg?.timeZone ?? order?.timeZone ?? 'America/New_York'
    const deliveryDateInfo = order?.deliveryDateInfo
    const currentLocalDateTime = DateTime.local({ zone: timeZone })
    // we are inferring that the pkg is likely at last mile/destination facility
    const pkgAtDestinationFacility = pkg?.eventLog[0]?.facility?.facilityId === order?.facilityId
    const pkgScheduledServiceDate: DateTime = DateTime.fromISO(pkg.scheduledServiceDate, { zone: timeZone })
    const pkgScheduledServiceDateIsStale: boolean = currentLocalDateTime >= pkgScheduledServiceDate

    const delayedResult: HeaderResult = {
      header: 'Delivery delayed.',
      subheader:
        'Your delivery is delayed. We apologize for the inconvenience. Check back within 24 hours for an updated delivery date.',
    }

    if (deliveryDateInfo) {
      const { date, dateType, hasBeenUpdated } = deliveryDateInfo
      const slaDate: DateTime = DateTime.fromISO(date, { zone: timeZone })
      const slaDateIsStale: boolean = currentLocalDateTime >= slaDate
      const datesStale: boolean = slaDateIsStale && pkgScheduledServiceDateIsStale
      // If the delivery date is delayed or the SLA date and package scheduled service date are both stale, return a delayed header
      if (dateType === DeliveryDateType.Delayed || datesStale) {
        return delayedResult
      }

      // Choose the date to display. At the destination facility we prefer the
      // package's scheduledServiceDate; otherwise we prefer the SLA date. We fall
      // back to the other date when the preferred one is stale.
      //
      // Critically, we never format an *invalid* Luxon DateTime: an invalid date
      // (e.g. a null scheduledServiceDate on a package lost in a warehouse) renders
      // as the literal string "Invalid DateTime". An invalid date is also not
      // considered "stale" (the `now >= invalidDate` comparison is a NaN compare,
      // which is false), so the stale-based fallback alone does not protect against
      // it — we must explicitly require validity here.
      const primary = pkgAtDestinationFacility ? pkgScheduledServiceDate : slaDate
      const primaryIsStale = pkgAtDestinationFacility ? pkgScheduledServiceDateIsStale : slaDateIsStale
      const secondary = pkgAtDestinationFacility ? slaDate : pkgScheduledServiceDate

      const chosenDate =
        primary.isValid && !primaryIsStale ? primary : secondary.isValid ? secondary : primary.isValid ? primary : null

      // No usable delivery date (e.g. lost in warehouse): show delayed rather than "Invalid DateTime".
      if (!chosenDate) {
        return delayedResult
      }
      const formattedDate = chosenDate.toFormat('cccc, LLLL d')

      if (dateType === DeliveryDateType.Estimated) {
        return hasBeenUpdated
          ? { header: `Now estimated by ${formattedDate}`, subheader: null }
          : { header: `Estimated delivery by ${formattedDate}`, subheader: null }
      }

      // Expected delivery date
      return hasBeenUpdated
        ? { header: `Now expected on ${formattedDate}`, subheader: null }
        : { header: `Expected delivery on ${formattedDate}`, subheader: null }
    }

    // If the package scheduled service date is in the past and there is no deliveryDateInfo or scheduled service date, return a delayed header
    if (
      (deliveryDateInfo === null || pkg?.scheduledServiceDate === null) &&
      currentLocalDateTime >= pkgScheduledServiceDate
    ) {
      return delayedResult
    }

    // If we don't have a delivery date info, we need to calculate it
    const { calculatedPackagesInformation } = order
    const { notDeliveredPackages } = calculatedPackagesInformation
    const { groupedByDeliveryDate } = notDeliveredPackages
    let deliveryString = ''

    groupedByDeliveryDate.forEach(_pkg => {
      if (_pkg?.trackingIds.includes(pkg.trackingId)) {
        const groupedDeliveryDate = DateTime.fromISO(_pkg.deliveryDate).setZone(pkg?.timeZone ?? order.timeZone!)
        // Skip invalid dates so we never surface "Invalid DateTime" in the header
        if (groupedDeliveryDate.isValid) {
          deliveryString = groupedDeliveryDate.toLocaleString(DateTime.DATE_MED)
        }
      }
    })

    if (deliveryString) {
      return {
        header: `Expected Delivery on ${deliveryString}`,
        subheader: null,
      }
    }

    // If still no delivery date, we need to use the delivery date from the order
    if (deliveryDate) {
      const orderDeliveryDate = DateTime.fromISO(deliveryDate).setZone(timeZone)
      // Only use the order delivery date when it parses to a valid DateTime
      if (orderDeliveryDate.isValid) {
        return {
          header: `Expected Delivery on ${orderDeliveryDate.toLocaleString(DateTime.DATE_MED)}`,
          subheader: null,
        }
      }
    }

    // If none of the above are set, we need to return the default header
    return {
      header: `Awaiting package from ${clientDisplayName || 'merchant'}.`,
      subheader:
        'Your order has been created. We will provide an estimated delivery date once the package has been received.',
    }
  }

  switch (mostRecentPkgStatus) {
    case PackageEventOperations.Created:
      return getPkgExpectedDeliveryDate({ pkg, order })
    case PackageEventOperations.DroppedOffAtVeho:
      return getPkgExpectedDeliveryDate({ pkg, order })
    case PackageEventOperations.PickedUpFromVeho:
      return {
        header:
          'Out for delivery!' +
          (pkg?.deliveryTimeWindow?.startsAt && pkg?.deliveryTimeWindow?.endsAt
            ? ` Arriving between ${DateTime.fromISO(pkg.deliveryTimeWindow?.startsAt)
                .setZone(pkg?.timeZone ?? order.timeZone!)
                .toLocaleString(DateTime.TIME_SIMPLE)} and ${DateTime.fromISO(pkg.deliveryTimeWindow?.endsAt)
                .setZone(pkg?.timeZone ?? order.timeZone!)
                .toLocaleString(DateTime.TIME_SIMPLE)}`
            : ''),
        subheader: null,
      }
    case PackageEventOperations.Delivered:
      return {
        header: 'Delivered!',
        subheader: deliveredTimestamp as string | null,
      }
    case PackageEventOperations.Misdelivered:
      return {
        header: 'Issue with Delivery',
        subheader: misdeliverySubheader(mostRecentPkgMessage!),
      }
    case PackageEventOperations.PackageArrivedAtFacility:
      return getPkgExpectedDeliveryDate({ pkg, order })
    case PackageEventOperations.PackageHadDeliveryIssue:
      return {
        header: packageHadDeliveryIssueHeader(pkg.eventLog[0].message!),
        subheader: packageHadDeliveryIssueSubheader(pkg.eventLog[0].message!),
      }
    case PackageEventOperations.PackageDepartedFromFacility:
      return getPkgExpectedDeliveryDate({ pkg, order })
    case PackageEventOperations.Discarded:
      return {
        header: 'Delivery Cancelled',
        subheader: `Your package from ${clientDisplayName} was unable to be delivered. We have let ${clientDisplayName} know. If you have questions or need help, please text Veho Customer Support at 68953.`,
      }
    case PackageEventOperations.ReturnedToClient:
      return {
        header: 'Delivery Cancelled',
        subheader: `Your order is being returned to ${clientDisplayName} after multiple unsuccessful delivery attempts.`,
      }
    case PackageEventOperations.Cancelled:
      return {
        header: 'Delivery Cancelled',
        subheader: 'This order has been cancelled.',
      }
    case PackageEventOperations.WrongFacilityByClient:
    case PackageEventOperations.WrongFacilityByVeho:
      return {
        header: 'Delivery Delayed',
        subheader: 'A transit error has delayed your package.',
      }
    default:
      return undefined
  }
}
